import React, { useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Paper,
  MenuItem,
  Typography,
  FormControlLabel,
  Checkbox,
  Button,
  IconButton,
  Tooltip
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import Header from "../../components/Header";

const ParametersDefinition = () => {
  const [formData, setFormData] = useState({
    procedure: "",
    startDate: "",
    endDate: "",
    active: true
  });

  const [parameter, setParameter] = useState({
    paramName: "",
    paramType: ""
  });

  const [rows, setRows] = useState([]);

  /* ================= FORM HANDLER ================= */

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleParameterChange = (e) => {
    const { name, value } = e.target;
    setParameter((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  /* ================= ADD PARAMETER ================= */

  const handleAdd = () => {
    if (!parameter.paramName || !parameter.paramType) return;

    setRows((prev) => [
      ...prev,
      {
        id: prev.length + 1,
        paramName: parameter.paramName,
        paramType: parameter.paramType,
        org: "",
        invoiceNumber: "",
        invoiceDate: ""
      }
    ]);

    setParameter({
      paramName: "",
      paramType: ""
    });
  };

  /* ================= DELETE ROW ================= */

  const handleDelete = (id) => {
    const updated = rows
      .filter((row) => row.id !== id)
      .map((row, index) => ({
        ...row,
        id: index + 1
      }));

    setRows(updated);
  };

  /* ================= ROW CHANGE ================= */

  const handleRowChange = (id, field, value) => {
    const updated = rows.map((row) =>
      row.id === id ? { ...row, [field]: value } : row
    );
    setRows(updated);
  };

  const handleSave = () => {
    const payload = {
      master: formData,
      parameters: rows
    };
    console.log("Saving Program:", payload);
  };

  const handleCancel = () => {
    setFormData({
      procedure: "",
      startDate: "",
      endDate: "",
      active: true
    });
    setParameter({
      paramName: "",
      paramType: ""
    });
    setRows([]);
  };

  return (
    <Box m="20px">
      <Header title="Parameters Definition" />

      {/* ================= MASTER SECTION ================= */}

      <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mt: 2 }}>
        <Grid container spacing={2}>

          <Grid item xs={3}>
            <TextField
              select
              label="Procedures"
              name="procedure"
              fullWidth
              value={formData.procedure}
              onChange={handleFormChange}
            >
              <MenuItem value="Proc1">Procedure 1</MenuItem>
              <MenuItem value="Proc2">Procedure 2</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={3}>
            <TextField
              label="Start Date"
              name="startDate"
              type="date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={formData.startDate}
              onChange={handleFormChange}
            />
          </Grid>

          <Grid item xs={3}>
            <TextField
              label="End Date"
              name="endDate"
              type="date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={formData.endDate}
              onChange={handleFormChange}
            />
          </Grid>

          <Grid item xs={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.active}
                  name="active"
                  onChange={handleFormChange}
                />
              }
              label={formData.active ? "Active" : "Inactive"}
            />
          </Grid>

          <Grid item xs={2}>
            <Button
              variant="contained"
              sx={{
                px: 4, backgroundColor: "#1976d2",
                "&:hover": {
                  backgroundColor: "#1565c0"
                },
                color: "#fff"
              }}
            >
              Register / Save
            </Button>
          </Grid>

        </Grid>
      </Paper>

      {/* ================= PARAMETER SECTION ================= */}

      <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mt: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} mb={2}>
          Add Parameter
        </Typography>

        <Grid container spacing={2} alignItems="center">

          <Grid item xs={4}>
            <TextField
              label="Parameter Name"
              name="paramName"
              fullWidth
              value={parameter.paramName}
              onChange={handleParameterChange}
            />
          </Grid>

          <Grid item xs={3}>
            <TextField
              select
              label="Parameter Type"
              name="paramType"
              fullWidth
              value={parameter.paramType}
              onChange={handleParameterChange}
            >
              <MenuItem value="Text">Text</MenuItem>
              <MenuItem value="Number">Number</MenuItem>
              <MenuItem value="Date">Date</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={2}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              sx={{ textTransform: "none" }}
            >
              Add
            </Button>
          </Grid>

        </Grid>
      </Paper>

      {/* ================= LIST SECTION ================= */}

      {rows.length > 0 && (
        <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mt: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>
            Parameter Details
          </Typography>

          {rows.map((row) => (
            <Grid container spacing={2} alignItems="center" key={row.id} mb={2}>

              <Grid item xs={2}>
                <Typography fontWeight={500}>
                  {row.paramName}
                </Typography>
              </Grid>

              <Grid item xs={3}>
                <TextField
                  select
                  label="Org"
                  fullWidth
                  value={row.org}
                  onChange={(e) =>
                    handleRowChange(row.id, "org", e.target.value)
                  }
                >
                  <MenuItem value="Org1">Org 1</MenuItem>
                  <MenuItem value="Org2">Org 2</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={3}>
                <TextField
                  select
                  label="Invoice Number"
                  fullWidth
                  value={row.invoiceNumber}
                  onChange={(e) =>
                    handleRowChange(row.id, "invoiceNumber", e.target.value)
                  }
                >
                  <MenuItem value="INV001">INV001</MenuItem>
                  <MenuItem value="INV002">INV002</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={2}>
                <TextField
                  label="Invoice Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  value={row.invoiceDate}
                  onChange={(e) =>
                    handleRowChange(row.id, "invoiceDate", e.target.value)
                  }
                />
              </Grid>

              <Grid item xs={1}>
                <Tooltip title="Remove">
                  <IconButton
                    onClick={() => handleDelete(row.id)}
                    sx={{
                      color: "#c62828",
                      "&:hover": { backgroundColor: "#ffebee" }
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Grid>

            </Grid>
          ))}

        </Paper>
      )}

      {/* ================= SAVE / CANCEL ================= */}

      <Box display="flex" gap={2} mt={3}>
        <Button
          variant="contained"
          onClick={handleSave}
          sx={{ textTransform: "none", px: 4 }}
        >
          Save
        </Button>

        <Button
          variant="outlined"
          onClick={handleCancel}
          sx={{ textTransform: "none", px: 4 }}
        >
          Cancel
        </Button>
      </Box>

    </Box>
  );
};

export default ParametersDefinition;